import { useLayoutEffect, useMemo, useRef } from 'react';

/**
 * For a context value built from a hook that returns a new object on every render:
 *  - function properties become identity-stable wrappers that always call the latest
 *    implementation (so they never go stale), and
 *  - the returned object only changes identity when one of the non-function values does.
 *
 * Without it, a `useMemo` over such an object never memoizes, and every consumer of the context
 * re-renders each time the provider does. The set of keys must not change between renders.
 */
function useStableValue<T extends Record<string, any>>(source: T): T {
  const latest = useRef(source);
  useLayoutEffect(() => {
    latest.current = source;
  });

  const keys = Object.keys(source);
  const callbacks = useMemo(() => {
    const wrappers: Record<string, (...args: any[]) => any> = {};
    keys.forEach((key) => {
      if (typeof source[key] === 'function') {
        wrappers[key] = (...args: any[]) => latest.current[key](...args);
      }
    });
    return wrappers;
    // The wrappers are created once: they read the latest implementation through the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dataKeys = keys.filter((key) => typeof source[key] !== 'function');
  const dataValues = dataKeys.map((key) => source[key]);

  return useMemo(() => {
    const value: Record<string, any> = { ...callbacks };
    dataKeys.forEach((key, i) => { value[key] = dataValues[i]; });
    return value as T;
    // dataKeys is constant for a given source shape, so the dependency list has a fixed length.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callbacks, ...dataValues]);
}

export default useStableValue;
